import React, { useRef, useState, useEffect } from "react";
import Layout from "../components/Layout";
import { useNavigate, useLocation } from "react-router-dom";
import { Document, Packer, Paragraph, HeadingLevel, Table, TableRow, TableCell, WidthType } from "docx";
import { downloadBlob } from "../nativeDownload";

export default function Manual() {
  const navigate = useNavigate();
  const location = useLocation();
  const contentRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  // Sidebar shortcuts like "Marker Verify Guide" link straight to
  // /manual#marker-verify — a plain <a href> anchor jump doesn't work here
  // because this is a client-side route, so there's nothing forcing a
  // scroll on arrival. Keyed on location.hash (not just mount) so clicking
  // the same guide link again while already on /manual still re-scrolls.
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }
  }, [location.hash]);

  const downloadAsWord = async () => {
    if (!contentRef.current) return;
    setExporting(true);
    try {
      const children = [];

      const addPara = (text, opts = {}) => {
        const clean = (text || "").replace(/\s+/g, " ").trim();
        if (!clean) return;
        children.push(new Paragraph({
          text: clean,
          heading: opts.heading,
          bullet: opts.bullet ? { level: 0 } : undefined,
          spacing: { after: 120 },
        }));
      };

      const buildTable = (tableEl) => {
        const rows = [];
        tableEl.querySelectorAll("tr").forEach((tr) => {
          const cells = [];
          const cellEls = tr.querySelectorAll("th,td");
          cellEls.forEach((cellEl) => {
            cells.push(new TableCell({
              children: [new Paragraph({ text: (cellEl.textContent || "").trim() })],
              width: { size: 100 / cellEls.length, type: WidthType.PERCENTAGE },
            }));
          });
          if (cells.length) rows.push(new TableRow({ children: cells }));
        });
        if (rows.length) children.push(new Table({ rows, width: { size: 100, type: WidthType.PERCENTAGE } }));
      };

      const walk = (node) => {
        Array.from(node.children).forEach((child) => {
          const tag = child.tagName.toLowerCase();
          if (tag === "h1") addPara(child.textContent, { heading: HeadingLevel.TITLE });
          else if (tag === "h2") addPara(child.textContent, { heading: HeadingLevel.HEADING_1 });
          else if (tag === "h3") addPara(child.textContent, { heading: HeadingLevel.HEADING_2 });
          else if (tag === "p") addPara(child.textContent);
          else if (tag === "table") buildTable(child);
          else if (tag === "ul" || tag === "ol") {
            Array.from(child.children).forEach((li) => addPara(li.textContent, { bullet: true }));
          } else if (child.children.length === 0 && (child.textContent || "").trim()) {
            addPara(child.textContent);
          } else {
            walk(child);
          }
        });
      };

      walk(contentRef.current);

      const doc = new Document({ sections: [{ children }] });
      const blob = await Packer.toBlob(doc);
      await downloadBlob(blob, "GolfSocietyManager-Manual.docx");
    } catch (e) {
      alert("Failed to generate Word document: " + e.message);
    } finally {
      setExporting(false);
    }
  };

  const Section = ({ title, id, children }) => (
    <div className="mb-6" id={id}>
      <h2 className="text-lg font-bold text-green-700 border-b border-green-200 pb-1 mb-3 mt-6">{title}</h2>
      {children}
    </div>
  );

  const Sub = ({ title, children }) => (
    <div className="mb-4">
      <h3 className="text-base font-bold text-green-800 mb-2">{title}</h3>
      {children}
    </div>
  );

  const Tip = ({ text }) => (
    <div className="bg-green-50 border-l-4 border-green-500 px-4 py-2 my-2 text-sm text-gray-700 italic">💡 {text}</div>
  );

  const Warn = ({ text }) => (
    <div className="bg-yellow-50 border-l-4 border-yellow-500 px-4 py-2 my-2 text-sm text-gray-700 italic">⚠️ {text}</div>
  );

  const Ul = ({ items }) => (
    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 mb-2">
      {items.map((item, i) => <li key={i} dangerouslySetInnerHTML={{__html: item}} />)}
    </ul>
  );

  const Ol = ({ items }) => (
    <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 mb-2">
      {items.map((item, i) => <li key={i} dangerouslySetInnerHTML={{__html: item}} />)}
    </ol>
  );

  const P = ({ text }) => <p className="text-sm text-gray-700 mb-2" dangerouslySetInnerHTML={{__html: text}} />;

  const Tbl = ({ headers, rows }) => (
    <div className="overflow-x-auto my-3">
      <table className="w-full text-sm border-collapse">
        <thead className="bg-green-800 text-white">
          <tr>{headers.map((h,i) => <th key={i} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row,i) => (
            <tr key={i} className={i%2===0?"bg-white":"bg-green-50"}>
              {row.map((cell,j) => <td key={j} className="px-3 py-1.5 border-b border-gray-100 text-xs">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const FormatBox = ({ icon, title, players, text }) => (
    <div className="border border-green-200 rounded-lg p-3 bg-white">
      <div className="font-bold text-green-800 text-sm mb-1">{icon} {title}</div>
      <div className="text-xs font-semibold text-green-600 mb-1">{players}</div>
      <div className="text-xs text-gray-600">{text}</div>
    </div>
  );

  return (
    <Layout>
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4">
          <span className="font-semibold text-sm">User Manual</span>
          <button onClick={downloadAsWord} disabled={exporting} className="ml-auto bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-semibold px-4 py-1.5 rounded">
            {exporting ? "Generating..." : "⬇ Download as Word"}
          </button>
          <button onClick={() => navigate(-1)} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-4 py-1.5 rounded">← Back</button>
        </div>
        <div ref={contentRef} className="flex-1 overflow-auto px-8 py-6" style={{maxWidth:"900px"}}>

          <div className="text-center mb-8 pb-6 border-b border-gray-200">
            <div className="text-5xl mb-2">⛳</div>
            <h1 className="text-3xl font-bold text-green-800">GolfSocietyManager — Complete User Guide</h1>
            <div className="text-xl font-bold text-yellow-600">Version 7.0.0</div>
            <div className="text-green-700 italic text-sm mt-1">Manage. Score. Enjoy.</div>
            <div className="mt-3 bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800 font-medium">
              20+ Game Formats &nbsp;•&nbsp; 6 Score Entry Layouts &nbsp;•&nbsp; Live Leaderboard &nbsp;•&nbsp; AI Course Import &nbsp;•&nbsp; Digital Marker Verification &nbsp;•&nbsp; Player Portal &nbsp;•&nbsp; Fixture & Event Planner &nbsp;•&nbsp; Score Locking &nbsp;•&nbsp; Inter-Club Match Play &nbsp;•&nbsp; Offline Desktop App &nbsp;•&nbsp; Android App
            </div>
            <div className="mt-2 text-xs text-gray-500 italic">Golf Society Manager is actively developed, with new features and improvements added on an ongoing basis.</div>
          </div>

          <div className="mb-6 bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
            <div className="font-bold text-green-800 text-base mb-1">🆕 Download the Desktop App — Play Fully Offline, Even With No Signal</div>
            <P text="Golf Society Manager now has a free Windows desktop app you can download from the website — a genuine differentiator, since it lets you run a complete competition day with zero internet connection, unlike most other golf society apps." />
            <P text="Download it using the Download link in the left sidebar (or from golfsocietymanager.com), install it, then activate it once with a free code — email support@golfsocietymanager.com with your Society name and we'll send you one. Codes currently need renewing roughly monthly while the app is free, ahead of this becoming a paid subscription." />
            <P text="Before you lose signal (e.g. in the car park), click Sync Now in the app's sidebar — this pulls down your players, groups and course, so the whole competition day works with zero internet from that point on. Once you're back in signal, click Sync Now again and every score entered offline is pushed straight back up to golfsocietymanager.com automatically. Full details in Section 13." />
          </div>

          <div className="mb-6 bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
            <div className="font-bold text-green-800 text-base mb-1">🆕 Install the Android App — Score Entry On the Course, From Your Phone or Tablet</div>
            <P text="Golf Society Manager also has a native Android app for tablets and phones — ideal for scoring out on the course. It isn't on the Google Play Store yet, so it's installed directly from a file your organiser provides rather than searched for in an app store. Works exactly like the website once signed in, with the same offline score-entry safety net as the Desktop App. Full details in Section 14." />
          </div>

          <Section title="0. 🔄 Browser Cache and Application Updates">
            <P text="Golf Society Manager is a web-based application. Occasionally, after a software update, your browser may continue to display an older version of the application from its local cache." />
            <Sub title="Desktop Computers">
              <P text="Try a hard refresh first:" />
              <Ul items={["Chrome: Ctrl + F5","Edge: Ctrl + F5","Firefox: Ctrl + F5"]} />
              <P text="If the issue persists:" />
              <Ol items={["Open your browser settings.","Locate Clear Browsing Data or Clear Cache.","Select Cached Images and Files.","Clear the cache and reopen Golf Society Manager."]} />
            </Sub>
            <Sub title="Mobile Devices">
              <Ol items={["Close Golf Society Manager completely.","Reopen the application.","If the issue remains, clear the browser cache from the browser settings menu."]} />
            </Sub>
            <Sub title="Keeping Up to Date">
              <P text="Golf Society Manager is regularly updated with new features and improvements. In most cases, updates are applied automatically when the application is reloaded. If you experience unusual behaviour after an update, clearing your browser cache will usually resolve the issue." />
              <P text="If problems continue, please contact: <a href='mailto:support@golfsocietymanager.com'>support@golfsocietymanager.com</a>" />
            </Sub>
          </Section>

          <Section title="1. Introduction — Current Features">
            <P text="GolfSocietyManager is a cloud-based platform for golf society organisers, with a free downloadable Windows desktop app and a native Android app for competition day. Accessible from any browser on any device, from the Android app on a phone or tablet, or fully offline via the Desktop App once activated and synced — it manages every aspect of your competition day, from setting up the event and assigning groups, to entering scores, viewing live leaderboards and archiving results permanently." />
            <Tbl headers={["Current Features"]} rows={[
              ["Supports 20+ game formats with automatic score entry and results layout selection"],
              ["Free Windows Desktop App — download, activate with a free code, then run a full competition day fully offline with no signal at all"],
              ["Every player has their own Tee Colour (White/Yellow/Red, Men/Ladies) — narrowed automatically to whatever tees the linked course actually has, when imported from the UK Golf Course Database"],
              ["Automatic Course Handicap calculation — Handicap Index adjusted for each player's own tee slope/rating, with an optional club-set ceiling (HCP CAP)"],
              ["Save Game / Load Game — save a full event setup as a reusable template"],
              ["Live public leaderboard — each society has its own web address at leaderboard.golfsocietymanager.com/your-society-slug/leaderboard"],
              ["AI-powered course import — photograph a scorecard and import it instantly"],
              ["UK Golf Course Database — look up a real course by name and import its official scorecard, including full tee-set data, instantly"],
              ["AI Validate Scores — one-click check for likely score-entry mistakes before you finish a round"],
              ["Automated nightly backups to your QNAP NAS"],
              ["Full competition archive with permanent results storage, plus multi-day combined results"],
              ["Player statistics across all archived competitions"],
              ["Twos & Fines tracking with running totals"],
              ["Multi-society support — each society manages its own data"],
              ["Digital Marker Score Verification — players verify partner scorecards digitally"],
              ["Player Portal at golfsocietymanager.com/choose-login — player login, results and verification"],
              ["Score locking — verified scores cannot be edited"],
              ["Admin unlock with a full, permanently logged audit trail"],
              ["Inter-Club Match Play with team names, running score and archive"],
              ["Super Admin dashboard — full system oversight"],
            ]} />
          </Section>

          <Section title="2. 📅 Multi-Day Competitions">
            <P text="Multi-day competitions let you run events across 2 or more days (e.g. a Captains Away trip) and generate combined overall results with totals across all days." />

            <Sub title="2.1 Setting Up Each Day">
              <P text="Set up and play each day as a normal single-day event, archiving it before moving to the next:" />
              <Ol items={[
                "Go to the Players page and set the event date, venue and format for that day",
                "Mark players as Playing and check/update their handicaps for that day",
                "Assign groups, enter scores and view results as normal",
                "On the Results page, click <strong>Archive Results</strong> to permanently save that day",
                "Repeat for each subsequent day, updating the date/venue/handicaps as needed",
              ]} />
            </Sub>

            <Sub title="2.2 Combining the Days">
              <Ol items={[
                "On the Results page, click <strong>Archive Multi Day</strong> (indigo button)",
                "Enter a Competition Name (e.g. Celtic Manor Captains Away 2026) and an optional Description",
                "In the Select Days list, tick each day in order — Day 1 first, then Day 2, etc. — the order you tick them determines the Day 1/2/3 labelling",
                "Click <strong>Save Multi Day Competition</strong>",
              ]} />
              <Warn text="You must Archive Results for each individual day before creating the multi-day combination. The Archive Multi Day modal will show No archived events found if no days have been archived yet." />
            </Sub>

            <Sub title="2.3 Viewing Combined Results">
              <Ol items={[
                "Click <strong>Multi Archive</strong> in the left navigation menu",
                "Select the competition from the left panel",
                "The results table shows each player with their score per day, total points or net score, total gross, and number of days played",
                "Players are ranked by total points (Stableford) or total net (Medal); players who missed a day are still included with their available day scores",
              ]} />
              <Tip text="You can create as many multi-day competitions as you like from the same pool of archived events. The same archived event can be included in multiple multi-day competitions." />
            </Sub>
          </Section>

          <Section title="3. 📅 Competition Day Workflow">
            <P text="Follow these steps in order for every competition day:" />
            <Tbl headers={["Step","Action","Screen"]} rows={[
              ["1","Set event date, venue, game format, course and HCP CAP","Players"],
              ["2","Mark players as Playing, Buggy, Captain and HCP Review (if applicable)","Players"],
              ["3","Auto-assign groups","Groups"],
              ["4","Assign pairs or teams (where applicable)","Teams"],
              ["5","Set up course (or import via AI)","Course"],
              ["6","Enter hole-by-hole scores as groups finish","Score Entry"],
              ["7","Save scores for each group","Score Entry"],
              ["8","Monitor live leaderboard","Leaderboard"],
              ["9","View final results","Results"],
              ["10","Archive competition for permanent records","Results → Archive"],
            ]} />
          </Section>

          <Section title="4. 🧭 Competition Formats Explained">
            <P text="Before the detailed screen-by-screen guide, here's a one-page overview of the six format families in GolfSocietyManager. Every later section refers back to these six." />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
              <FormatBox icon="👤" title="Individual" players="Individual" text="One player, one scorecard. Singles Stableford, Medal, Stroke Play, Skins." />
              <FormatBox icon="👥" title="Better Ball" players="Pair" text="Both players in a pair play their own ball — the better score counts. Better Ball Stableford/Medal/Stroke Play, Fourball." />
              <FormatBox icon="👨‍👨‍👦" title="Team Aggregate" players="Team (3–4)" text="All team members submit a score; the required number count towards the team total. Team Stableford, Team Medal." />
              <FormatBox icon="🎯" title="Texas Scramble" players="Team (3–4)" text="All team members tee off, play the best drive, repeat until holed. One team score entered per hole." />
              <FormatBox icon="🤝" title="Alternate Shot" players="Pair" text="Two players share one ball, playing alternate shots. Foursomes, Greensomes." />
              <FormatBox icon="⚔️" title="Match Play" players="Side A vs Side B" text="Head-to-head, hole by hole — any of the above can be played as Match Play instead of Stableford/Medal." />
            </div>
          </Section>

          <Section title="5. 🖥️ Screen Guide">
            <Sub title="5.1 👤 Players Screen">
              <P text="The Players screen is your master list of all society members. Set up each event here." />
              <Tbl headers={["Column","Purpose"]} rows={[
                ["Member","Player full name — surname first for alphabetical sorting"],
                ["Handicap","WHS Handicap Index. Click to edit. Press Enter to save."],
                ["Tee Colour","The player's own tee (White / Yellow / Red) — options are filtered automatically to whatever the linked Course actually has recorded."],
                ["Course HCP","Calculated automatically from Handicap Index, Tee Colour and the linked course's slope/rating — see Section 7."],
                ["Play HCP","Course HCP with the event's format allowance applied — see Section 7."],
                ["Playing","Tick to include in today's competition"],
                ["Buggy","Buggy players are paired together in groups"],
                ["Captain","Only ONE captain. Always placed in Group 1 when Auto Group is run."],
                ["Vice Captain","Only ONE vice-captain. Leads Group 1 if the captain isn't playing, otherwise Group 2."],
                ["Comments","Free text — dietary, medical, car sharing notes"],
                ["HCP Review","Tick to submit this player's score for digital marker verification — see Section 12 for eligible formats and the full verification workflow"],
              ]} />
              <P text="The toolbar shows: Event date, Venue, Format, Course and HCP CAP — see Section 7 for how these feed into each player's Course HCP and Play HCP." />
              <Tip text="Press Enter after editing a handicap or comment to save instantly." />
              <Tip text="Deleting a player who already has scores or results on record doesn't erase that history — they're deactivated instead, so every past score and result stays exactly as it was, and they simply won't appear for future events." />
              <Sub title="💾 Save Game / Load Game">
                <P text="Two toolbar buttons let you save and reuse a complete event setup — handy for a regular fixture where the same players, format and course come up again and again." />
                <Ul items={[
                  "<strong>Save Game</strong> — enter a name and save the current players (with their handicaps at the time), event format and settings as a template.",
                  "<strong>Load Game</strong> — shows your saved games. Click one to load it — this replaces the current event and overwrites each saved player's handicap with the value that was saved at the time.",
                  "Each saved game also has an <strong>Update</strong> button, so a recurring fixture can be kept up to date in place instead of piling up near-duplicate saves.",
                  "A delete option is available next to each saved game in the Load Game list.",
                ]} />
                <Warn text="Loading a saved game overwrites the current event and player handicaps — you'll be asked to confirm first." />
              </Sub>
            </Sub>

            <Sub title="5.2 👥 Groups Screen">
              <P text="Groups (the foursomes/threesomes that go out on the course together) are built automatically by clicking Auto Group, in this order:" />
              <Ol items={[
                "Groups of 3 and 4 only — never a group of 1 or 2. Groups of 3 are placed first so they tee off ahead of the 4-balls.",
                "The Captain is placed in Group 1.",
                "The Vice-Captain is placed in Group 1 if the Captain isn't playing that day, otherwise Group 2.",
                "Buggy players are always paired together in the same group — any group can contain a buggy pair, not just 4-balls.",
                "Everyone else is filled in, balanced against Team A/B where teams are already set, otherwise in handicap order.",
              ]} />
              <Tip text="You can manually change any group number afterwards. Each group shows a bingo number for draw purposes." />
              <P text="Marker assignment for HCP Review players is no longer set up here — see Section 12.4 for how players declare their own marker at the tee." />
              <Sub title="Drag-and-Drop & Removing a Drop-Out">
                <Ul items={[
                  "<strong>Drag-and-drop:</strong> click and hold the ⠿ handle at the start of a player's row, drag it onto any player already in the group you want to move them to, and drop — the move saves immediately.",
                  "<strong>Removing a drop-out:</strong> click the small ✕ next to a player's name and confirm — they disappear from Groups immediately, and their Playing tick on the Players screen is cleared automatically (this only unticks Playing, it doesn't delete the player).",
                ]} />
                <Warn text="Drag-and-drop needs a mouse or trackpad — works on the website and the V5 Desktop App, but not by touch on a phone or tablet. Use the group number field (click to edit) there instead." />
              </Sub>
            </Sub>

            <Sub title="5.3 🤝 Teams Screen">
              <P text="Used to organise players into Team A and Team B. What this means depends on the format: for Match Play formats these act as Side A / Side B; for pair formats (Better Ball, Foursomes, Greensomes) they simply group players into their partnerships — see Section 4 for a refresher on the format families." />
              <Tip text="You can rename Team A and Team B to your society's actual team names." />
              <Warn text="Teams must be saved before entering scores. Click Save Teams after all assignments." />
            </Sub>

            <Sub title="5.4 ⛳ Course Screen">
              <P text="Stores hole-by-hole details for each course. Select a course to link it to the active event." />
              <Tbl headers={["Field","Description"]} rows={[
                ["Hole","Hole number (1–18)"],
                ["Par","Par for the hole (3, 4 or 5)"],
                ["Stroke Index","Handicap stroke index (1–18, where 1 is the hardest)"],
              ]} />
              <Sub title="UK Golf Course Database (recommended)">
                <P text="The <strong>UK Golf Database</strong> button looks up real, published data for thousands of UK golf clubs — no photograph needed, and no risk of a typo, since it's pulled from a published course database rather than typed in or read from a photo." />
                <Ol items={[
                  "Go to Course Setup and click <strong>UK Golf Database</strong>",
                  "Type the club's name and click Search",
                  "If more than one club matches, pick the right one — town and county are shown to help tell apart similarly-named clubs",
                  "If the club has more than one course, pick the one you play",
                  "Pick which tees you play from (White, Yellow, Red, etc.) — every tee at the course is listed with its own par, rating and slope",
                  "Review the full 18-hole table — par, stroke index and yardage are all real, published figures",
                  "Click <strong>Create Course</strong> to add it, ready to select on the Players page",
                ]} />
                <Tip text="Courses imported this way also automatically fetch a real aerial photo of the course, shown above the course details." />
                <Warn text="If a club genuinely isn't in the database, use Import Image below instead." />
              </Sub>
              <Sub title="AI Course Import">
                <Ol items={[
                  "Take a clear photograph of the course scorecard",
                  "Go to the Course screen and click <strong>Import Image</strong>",
                  "Select your image file",
                  "The AI (powered by Claude) reads all 18 holes automatically",
                  "Check the imported data and correct any errors",
                ]} />
                <Tip text="Works best with a clear, well-lit photograph of the printed scorecard." />
              </Sub>
            </Sub>

            <Sub title="5.5 ✍️ Score Entry Screen">
              <P text="The correct score entry screen is selected automatically based on the competition format (see Section 4). No manual selection is required." />
              <Tbl headers={["Competition Format","Score Entry Layout"]} rows={[
                ["Singles Stableford, Medal, Stroke Play, Skins","Individual — one row per player."],
                ["Better Ball Stableford, Better Ball Medal, Better Ball Stroke Play, Fourball Better Ball","Pairs (Better Ball) — each player enters their own score; the best net score counts for the pair."],
                ["Team Stableford, Team Medal","Team Aggregate — all team members enter individual scores; the required number of scores count towards the team total."],
                ["Texas Scramble","Team Scramble — one gross team score is entered for each hole."],
                ["Foursomes Stableford, Foursomes Medal, Greensomes Stableford, Greensomes Medal","Alternate Shot — one gross score is entered for each pair on every hole."],
                ["Any Match Play Format","Match Play — hole-by-hole scores are entered for each side and the match result is calculated automatically."],
              ]} />
              <P text="The current score entry layout is displayed at the top-left of the screen — Individual Score Entry, Better Ball Score Entry, Team Aggregate Score Entry, Texas Scramble Score Entry, Alternate Shot Score Entry, or Match Play Score Entry." />
              <Sub title="Entering Scores">
                <Ol items={[
                  "Select the player, pair or team from the drop-down list.",
                  "Enter the gross score for each hole.",
                  "Net scores, Stableford points (where applicable) and running totals are calculated automatically.",
                  "Click <strong>Save Scores</strong> to save the scorecard.",
                  "The Leaderboard is updated immediately.",
                ]} />
              </Sub>
              <Sub title="Overriding Play HCP (Edit With Caution)">
                <P text="On the Individual Score Entry screen, click a player's Play HCP value to manually override it for this event only — the number you type replaces the automatically calculated Play HCP outright, with no further allowance math applied on top. Clear the box (leave it blank) to remove the override and go back to the calculated value." />
                <Warn text="This is marked Edit With Caution for a reason — it bypasses the normal Course HCP → format allowance calculation entirely for that one player, in that one event. Use it only for a genuine, deliberate exception, not routine corrections (fix the player's Handicap Index, Tee Colour or the event's HCP CAP instead — see Section 7)." />
                <Tip text="An overridden value shows a red asterisk. This works the same way both online and in the offline Desktop App, and syncs between the two like any other score entry change." />
              </Sub>
              <Sub title="✓ AI Validate Scores">
                <P text="On the individual Score Entry screen, the <strong>✓ Validate Scores</strong> button asks Claude to review every score entered so far for the current event and flag anything that looks like a likely typing mistake — not a bad golf shot." />
                <Ol items={[
                  "Enter scores as normal during or after the round",
                  "Click <strong>✓ Validate Scores</strong> at any point — you don't need every hole filled in first",
                  "A summary appears showing either \"No likely errors found\", or a list of flagged holes with the player, hole number, gross score entered, and a short reason it looks suspicious",
                ]} />
                <Tip text="Advisory only — it never changes any scores itself. It's a second pair of eyes for the organiser before scores are locked or archived, not an automatic correction." />
              </Sub>
              <Sub title="👻 Ghost Player">
                <P text="Ghost solves the problem of an uneven number of players: if one team or group is short a player (3 against everyone else's 4), that side is at a real disadvantage — fewer scores to pick the best from on every hole. Ghost borrows a real, already-playing player's own real score to even things up." />
                <Ul items={[
                  "<strong>The Ghost is not a fake player and does not get a made-up score.</strong> One real player already in the competition is chosen as the Ghost. They keep playing and scoring normally in their own group — and their real score is also copied in as the missing member of whichever team or group is short.",
                  "<strong>Selection is automatic.</strong> Click <strong>👻 Ghost</strong> on the Groups screen and the system picks whichever playing player has a handicap closest to the average handicap of everyone playing that day — this keeps the borrowed score fair rather than picking the best or worst player in the field.",
                  "<strong>One Ghost per competition.</strong> The same Ghost is reused for every short-handed group or team on the day, not a different one for each.",
                  "Remove the Ghost at any time with the same button (now showing <strong>Remove Ghost</strong>) — nothing is deleted or affected except the pairing itself.",
                ]} />
                <Tbl headers={["Available for", "Not available for"]} rows={[
                  ["Better Ball Stableford, Better Ball Medal, Better Ball Stroke Play, Fourball Better Ball Stableford, Team Stableford, Team Medal", "Singles formats, Skins, Texas Scramble, Foursomes, Greensomes, and all Match Play formats"],
                ]} />
                <P text="Match Play formats don't offer Ghost because a short-handed side is simply how that match is played — there's no team total for a borrowed score to feed into. The same reasoning excludes Foursomes, Greensomes and Texas Scramble, which score as a single combined pair/team effort rather than a pool of individual scores." />
                <Tip text="The Ghost button only appears on the Groups screen when the event's format is Ghost-eligible and there's actually an uneven group to fix." />
              </Sub>
              <Sub title="Match Play Score Entry">
                <Ol items={[
                  "Select the match from the drop-down list.",
                  "Enter the gross score for Side A and Side B on each hole.",
                  "The winner of each hole and the current match status (e.g. 2 Up, All Square) are calculated automatically, using the allowance for the match's specific format.",
                  "Once the match is decided mathematically, the remaining holes are locked — see Section 6.6 for the format list and Section 7.3 for exactly how each format's allowance works.",
                ]} />
                <Tbl headers={["Match Play Format","Allowance used at the tee"]} rows={[
                  ["Singles","Full Course HCP each. Lowest in the match plays scratch; the other player gets the difference."],
                  ["Fourball / Better Ball","Each player's own Course HCP × 90%. Lowest Play HCP among all 4 plays scratch; the other three get the difference."],
                  ["Foursomes","50% of each side's combined Course HCP. Lower side plays scratch; the other side gets the difference."],
                  ["Greensomes","60% lower + 40% higher Course HCP per side. Lower side plays scratch; the other side gets the difference."],
                ]} />
              </Sub>
            </Sub>

            <Sub title="5.6 🏆 Results Screen">
              <P text="Results layout is automatically selected by game format, matching the same six format families from Section 4." />
              <Tbl headers={["Results Layout","Used For","Primary Sort"]} rows={[
                ["Individual Results","Singles Stableford, Medal, Stroke Play","Highest points or lowest net"],
                ["Individual Results","Skins","Most skins won"],
                ["Better Ball Results","Better Ball, Fourball Better Ball","Highest combined best ball points"],
                ["Team Results","Team Stableford, Team Medal","Highest team aggregate points"],
                ["Texas Scramble Results","Texas Scramble","Highest points (default) or lowest net (Texas Scramble Medal)"],
                ["Pair Results","Foursomes, Greensomes","Highest pair points or lowest pair net"],
                ["Match Play Results","All Match Play formats","Match result (e.g. Ashmore won 3&2)"],
              ]} />
              <Sub title="Countback (Tied Positions)">
                <Ol items={["Back 9 — points scored on holes 10–18","Last 6 — points scored on holes 13–18","Last 3 — points scored on holes 16–18","Last 1 — points scored on hole 18"]} />
              </Sub>
              <Warn text="Always archive a competition before starting a new one." />
            </Sub>

            <Sub title="5.7 📊 Leaderboard Screen">
              <P text="Shows live standings. Position only shows when a player has finished all 18 holes. Updates automatically every 30 seconds, no login required." />
              <div className="text-center font-bold text-green-700 text-lg my-2">leaderboard.golfsocietymanager.com</div>
              <Tip text="Share this URL via WhatsApp for live updates on the course, or open it on a smart TV browser in the clubhouse for a full-screen display." />
            </Sub>

            <Sub title="5.8 💾 Archive Screen">
              <P text="Stores all past competition results permanently. Click any event to see full results." />
              <Ul items={["Competition name, format, date, venue and course shown","Click a row to view full results","Delete button removes archived event permanently"]} />
            </Sub>

            <Sub title="5.8b 💾 Multi Archive Screen">
              <P text="Shows combined results for multi-day competitions built from your individually archived events (see Section 2d.1 for the full workflow)." />
              <Ul items={[
                "Left panel lists every multi-day competition you've created",
                "Select one to see the combined results table — each player's score per day, plus totals",
                "Players are ranked by total points (Stableford) or total net (Medal)",
                "A player who missed a day still appears, ranked using only their available day scores",
              ]} />
              <Tip text="Create the multi-day combination itself from the Results screen (Archive Multi Day button) — this screen is just for viewing/reviewing them afterward." />
            </Sub>

            <Sub title="5.9 📈 Player Stats Screen">
              <P text="Shows historical statistics for each player across all archived competitions." />
              <Tbl headers={["Statistic","Description"]} rows={[
                ["Competitions Played","Total archived competitions participated in"],
                ["Average Points","Average Stableford points across all competitions"],
                ["Best Score","Highest points total in any single competition"],
                ["Worst Score","Lowest points total in any single competition"],
                ["Wins","Number of competitions won (1st place)"],
                ["Handicap Trend","Handicap change over time"],
              ]} />
              <Tip text="Player Stats are built from the Archive. Archive every competition for best results." />
              <P text="Click any player's row to open their own season trends — points per event, handicap, gross/net score and rank, each as a chart across the season — alongside a table of every round. From there, <strong>Export PDF</strong> saves that player's own chart pack as a PDF, and an organiser can use <strong>Email Season Stats</strong> to send every selected player their own PDF by email in one go (handy at season's end)." />
              <Sub title="🏆 Season Points (Order of Merit) & Season Championship">
                <P text="The Player Stats page also tracks a running season points total, in the style of a club Order of Merit — each competition's places score points, with the top scorer leading. A Season Championship view alongside it shows each player's best 5 results for the season, for deciding the overall winner." />
              </Sub>
            </Sub>

            <Sub title="5.10 🪙 Twos & Fines Screen">
              <P text="Records players who score a 2 during the round. Twos are detected automatically." />
              <Tbl headers={["Feature","Description"]} rows={[
                ["Twos list","All players who scored a 2 on any hole, with hole number"],
                ["Fine amount","Set the fine per two — e.g. £2 per two scored"],
                ["Running total","Total fines collected from twos during the round"],
                ["Auto-detection","Detected automatically when score of 2 entered in Score Entry"],
              ]} />
              <Tip text="The Twos & Fines screen updates in real time as scores are entered." />
            </Sub>
            <Sub title="5.11 🏆 Winners and Losers Screen">
              <P text="Displays the final prize winners for the competition with gold, silver and bronze highlighting. Also manages the Losers Raffle for non-prize players." />
              <Tbl headers={["Feature","Description"]} rows={[
                ["Prize Winners","1st, 2nd and 3rd place automatically calculated with full countback to separate tied players"],
                ["Gold / Silver / Bronze","1st place highlighted gold, 2nd silver, 3rd bronze"],
                ["Nearest Pin","Enter the nearest pin winner — excluded from the Losers Raffle"],
                ["Nearest Pin in 2","Enter the nearest pin in 2 winner — excluded from Losers Raffle"],
                ["Longest Drive","Enter the longest drive winner — excluded from Losers Raffle"],
                ["Save Prizes","Saves all prize winners to the database"],
                ["Losers Raffle","All players not in top 3 and not prize winners, each assigned a random raffle number"],
                ["Spin The Wheel","Randomly selects one winner from the Losers Raffle and highlights them in yellow"],
              ]} />
              <Tip text="Prize winners entered on the Results screen are automatically saved when you archive results." />
            </Sub>
          </Section>

          <Section title="6. ⛳ Game Formats">
            <P text="GolfSocietyManager supports a wide range of game formats across the six families introduced in Section 4. The correct score entry and results layout are selected automatically." />

            <Sub title="6.1 👤 Individual Formats">
              <Tbl headers={["Format","Players","Scoring","Winner"]} rows={[
                ["Singles Stableford","Individual","Stableford points per hole based on handicap","Highest points total"],
                ["Singles Medal","Individual","Net score (gross minus handicap)","Lowest net total"],
                ["Singles Stroke Play","Individual","Gross strokes total","Lowest gross total"],
                ["Skins","Individual","Lowest unique net (handicap-adjusted) score wins each hole","Most skins won"],
              ]} />
            </Sub>

            <Sub title="6.2 👥 Better Ball Formats">
              <Tbl headers={["Format","Players","Scoring","Winner"]} rows={[
                ["Better Ball Stableford","Pair","Both play own ball. Best Stableford points per hole count.","Highest best ball points"],
                ["Fourball Better Ball Stableford","Pair","Same as Better Ball — 4 players in two pairs","Highest best ball points"],
                ["Better Ball Medal","Pair","Best net score per hole from each pair","Lowest best net"],
                ["Better Ball Stroke Play","Pair","Best gross score per hole from each pair","Lowest best gross"],
              ]} />
            </Sub>

            <Sub title="6.3 👨‍👨‍👦 Team Aggregate Formats">
              <Tbl headers={["Format","Players","Scoring","Winner"]} rows={[
                ["Team Stableford","Team (3–4)","All players submit scores. Team total Stableford points.","Highest team total"],
                ["Team Medal","Team (3–4)","All players submit scores. Team total net strokes.","Lowest team net"],
              ]} />
              <P text="Count Rule: Best 1, Best 2 (most common), Best 3, or All 4 scores count per hole." />
            </Sub>

            <Sub title="6.4 🎯 Texas Scramble">
              <P text="All team members tee off. Team selects the best drive and all play from that spot. Repeat until holed. One team gross score entered per hole." />
              <P text="Two scoring variants are available: the default <strong>Texas Scramble</strong> converts each hole's net score to Stableford-style points (winner: highest points); <strong>Texas Scramble Medal</strong> instead totals net strokes directly (winner: lowest net). Both use the same handicap allowance table (Section 7.3)." />
            </Sub>

            <Sub title="6.5 🤝 Foursomes & Greensomes (Alternate Shot)">
              <Tbl headers={["Format","Players","Scoring","Winner"]} rows={[
                ["Foursomes Stableford","Pair","Alternate shots.","Highest Stableford points"],
                ["Foursomes Medal","Pair","Alternate shots.","Lowest net score"],
                ["Greensomes Stableford","Pair","Both drive, choose best, then alternate shots.","Highest Stableford points"],
                ["Greensomes Medal","Pair","Both drive, choose best, then alternate shots.","Lowest net score"],
              ]} />
              <P text="See Section 7.3 for the handicap allowance used in these formats." />
            </Sub>

            <Sub title="6.6 ⚔️ Match Play Formats">
              <Tbl headers={["Format","Players","Competing Parties"]} rows={[
                ["Singles Match Play","Individual","Individual vs individual"],
                ["Better Ball Match Play","Pair","Two players vs two players — best score counts per hole"],
                ["Fourball Match Play","Pair","Two players vs two players — same as Better Ball Match Play"],
                ["Foursomes Match Play","Pair","Pair vs pair — alternate shots"],
                ["Greensomes Match Play","Pair","Pair vs pair — both drive, choose best, then alternate shots"],
              ]} />
              <P text="The winner of each hole is not simply whoever scores lower gross — handicap strokes are applied first, on the relevant Stroke Index holes, and the resulting (net) score for that hole decides the winner:" />
              <Ul items={[
                "Each side's match play strokes are calculated as in Section 7.3 (e.g. in Singles, the lowest Course HCP in the match plays scratch and the other player receives the difference in strokes).",
                "Those strokes are given on the hardest holes first, in Stroke Index order — a player receiving 5 strokes gets one on each of the 5 lowest-SI holes, for example.",
                "On a hole where a player receives a stroke, that stroke is deducted before comparing scores for that hole — so a gross 5 with a stroke on that hole counts as a net 4 for match play purposes.",
                "Winner of each hole is whichever side has the lower resulting score after strokes are applied.",
                "Halved holes where both sides' resulting scores are the same.",
                "Running match status: AS, 1UP, 2UP etc.",
                "Close-out: when the match is mathematically won, remaining holes are locked.",
              ]} />
            </Sub>

            <Sub title="6.7 🎲 Skins — Detailed Rules">
              <Ul items={[
                "The player with the LOWEST net (handicap-adjusted) score on each hole wins that skin",
                "BUT only if their score is unique — if two players tie, nobody wins that hole",
                "Tied holes carry over — the skin rolls to the next hole, making it worth more",
                "A player can win multiple skins from a single hole if carry-overs have accumulated",
              ]} />
              <Tip text="Hole 1 tied: skin carries to Hole 2 (worth 2 skins). Winner of Hole 2 with unique score collects 2 skins." />
            </Sub>
          </Section>

          <Section title="7. 🧮 Handicap">
            <Sub title="7.1 Player Tee Selection & Automatic Handicap Calculation">
              <P text="GSM calculates each player's handicap through a fixed chain, matching the on-screen columns exactly:" />
              <Ol items={[
                "<strong>WHS HCP</strong> — the player's official Handicap Index, set on the Players screen.",
                "<strong>Tee</strong> — each player selects their own tee (e.g. White, Yellow, Red) from whatever the linked course's own data actually supports — the options narrow automatically to the tees and genders that course has recorded, so you can't pick a tee the course doesn't have.",
                "<strong>Course HCP</strong> — calculated automatically from WHS HCP using that tee's slope and rating. If the event has an HCP CAP set (7.2), Course HCP is capped at that value.",
                "<strong>Play HCP</strong> — Course HCP with this event's format allowance applied (7.3) — this is the number actually used for scoring.",
              ]} />
              <Tip text="Change a player's course or tee on the Players screen and Course HCP and Play HCP recalculate automatically everywhere they're shown." />
            </Sub>

            <Sub title="7.2 HCP CAP">
              <P text="HCP CAP is an optional ceiling set on the Players screen toolbar for the current event. It limits how high a player's Course HCP can go before format allowances are applied — it is a cap, not a percentage multiplier." />
              <Tbl headers={["Setting","Effect"]} rows={[
                ["Blank / not set","No cap — Course HCP is used as calculated."],
                ["e.g. 28","Any player whose Course HCP calculates above 28 is capped at 28 for this event."],
              ]} />
              <Tip text="HCP CAP affects Course HCP only. Each format's own allowance (7.3) is applied on top, not instead." />
            </Sub>

            <Sub title="7.3 Handicap Allowances by Format">
              <P text="Each format applies its own allowance to Course HCP (after any HCP CAP) to produce Play HCP:" />
              <Tbl headers={["Format","Allowance"]} rows={[
                ["Singles Stableford / Medal / Stroke Play","95% of Course HCP"],
                ["Singles Stableford (Full Handicap)","100% of Course HCP"],
                ["Team Stableford / Team Medal","95% of Course HCP"],
                ["Better Ball Stableford / Medal / Stroke Play, Fourball Better Ball","85% of Course HCP"],
                ["Skins","100% of Course HCP — no reduction"],
                ["Foursomes (Stableford/Medal)","50% of the pair's combined Course HCP"],
                ["Greensomes (Stableford/Medal)","60% of the lower player's Course HCP + 40% of the higher player's Course HCP"],
                ["Texas Scramble (both variants)","25% / 20% / 15% / 10% of each player's Course HCP, weighted from lowest to highest (30% / 20% / 10% for 3 players)"],
              ]} />
              <P text="Match Play formats work differently: rather than each side simply playing off a percentage, the lowest-handicapped side or player in the match plays off scratch, and everyone else receives the difference as strokes:" />
              <Tbl headers={["Match Play Format","How the allowance works"]} rows={[
                ["Singles Match Play","Full Course HCP for each player. The lowest Course HCP in the match plays scratch; the other player receives the difference in strokes."],
                ["Fourball / Better Ball Match Play","Each player's own Course HCP × 90%. The lowest resulting Play HCP among all 4 players plays scratch; the other three each receive the difference."],
                ["Foursomes Match Play","50% of each side's combined Course HCP. The lower side plays scratch; the other side receives the difference."],
                ["Greensomes Match Play","60/40 blend (as above) for each side. The lower side plays scratch; the other side receives the difference."],
              ]} />
            </Sub>

            <Sub title="7.4 Automatic Handicap Adjustment (Optional, Changeable Settings)">
              <P text="This is a separate, optional feature for societies that self-manage their members' Handicap Index rather than importing it from an official WHS source — it is not part of the WHS calculation chain in 7.1–7.3 above, and only affects the WHS HCP value itself, before any of that chain runs." />
              <P text="Turned on in Changeable Settings, it lets a society auto-adjust a player's WHS HCP after each archived round, based on the points they scored:" />
              <Ul items={[
                "<strong>New member cap</strong> — a new player starts on their club handicap, or this number, whichever is lower.",
                "<strong>Maximum handicap</strong> — no player's WHS HCP can ever be edited above this ceiling.",
                "<strong>Points bands</strong> — a configurable table (points scored → handicap change) applied automatically every time a round is archived.",
                "<strong>Winner's extra reduction</strong> — the competition winner loses an additional point on top of their band adjustment.",
              ]} />
              <Tip text="This is entirely optional per society (off by default) and separate from any official handicap review process — see Section 12 for the digital marker verification workflow." />
            </Sub>
          </Section>

          <Section title="8. 🔐 Registration & Login">
            <Sub title="8.1 New Society Registration">
              <Ol items={[
                "Go to golfsocietymanager.com",
                "Click <strong>Features</strong> to see Landing Page 2",
                "Click <strong>Sign In / Register</strong>",
                "Click <strong>Register here</strong>",
                "Enter name, email and password",
                "Enter your society name",
                "Read and tick the Licence Agreement",
                "Click <strong>Register</strong> — you are taken to the Players screen",
              ]} />
              <Tip text="The first thing to do after registering is add your players, then set up your first course." />
            </Sub>
            <Sub title="8.2 Returning Users">
              <P text="Visit golfsocietymanager.com, click Features, then Sign In. After logging in you go directly to the app." />
            </Sub>
            <Sub title="8.3 Forgotten Password">
              <P text="Click Forgot password? on the login screen. Enter your email and a reset link will be sent. Click the link and enter a new password." />
            </Sub>
          </Section>

          <Section title="9. 🔒 Backup & Security">
            <Sub title="9.1 Automated Backup">
              <P text="GolfSocietyManager automatically backs up all data every night at 3:00 AM. Includes full database dump and all application files. If you have a QNAP NAS, backups are pulled automatically at 3:30 AM via WebDAV." />
            </Sub>
            <Sub title="9.2 Export & Import Database">
              <Ul items={["<strong>Export Database</strong> — downloads a full backup to your computer","<strong>Import Database</strong> — restores from a previously exported backup"]} />
              <Warn text="Always Export Database before making major changes. Import Database overwrites all current data." />
            </Sub>
            <Sub title="9.3 Quick Save">
              <P text="The Quick Save button (top right of every screen) creates an instant timestamped backup on the server. Click before and after every competition day." />
            </Sub>
          </Section>

          <Section title="10. 💡 Tips & Best Practices">
            <Tbl headers={["Situation","Best Practice"]} rows={[
              ["Before competition day","Mark players as Playing. Check handicaps. Set game format. Verify course is linked."],
              ["Groups setup","Run auto-assign first. Then manually adjust. Always check captain is in Group 1."],
              ["Score entry","Enter scores hole by hole as each group finishes. Save regularly."],
              ["Live leaderboard / TV display","See Section 5.7 — share the URL via WhatsApp, or display full-screen on a smart TV."],
              ["After competition","Archive results before starting a new event."],
              ["New course","See Section 5.4 — use AI Course Import with a scorecard photo."],
              ["Playing offline (no signal)","See Section 13 — install the V5 Desktop App and Sync Now before you lose connection."],
            ]} />
          </Section>

          <Section title="11. 📞 Support & Contact">
            <Tbl headers={["Contact","Details"]} rows={[
              ["Website","golfsocietymanager.com"],
              ["Email","support@golfsocietymanager.com"],
              ["Public Leaderboard","leaderboard.golfsocietymanager.com"],
              ["Player Verification Portal","golfsocietymanager.com/choose-login"],
            ]} />
          </Section>

          <Section id="marker-verify" title="12. ✅ Digital Marker Score Verification">
            <P text="GolfSocietyManager includes a digital marker verification system. It allows a playing partner (the marker) to confirm a player's scorecard after the round — once verified, the score is locked and every change is permanently logged." />

            <Sub title="12.1 What is HCP Review?">
              <P text="Under the World Handicap System (WHS), scores submitted for handicap review must be verified by the player's marker — the person who marked their card during the round. GSM automates this entire process digitally." />
              <P text="Only three formats are eligible for HCP Review — Singles Stableford, Singles Medal and Singles Stroke Play — and the HCP Review column on the Players screen only appears for these." />
            </Sub>

            <Sub title="12.2 Player Registration">
              <P text="Before the system can be used, each player must create a Player Account. This is separate from the organiser account." />
              <Ol items={[
                "Visit golfsocietymanager.com/choose-login",
                "Click I am a Player then New player Register here",
                "Enter your First Name, Last Name, Email and Password",
                "Your name must match exactly what your organiser has on record in GSM",
                "If matched successfully you will be linked to your player profile automatically",
                "If not matched, contact your organiser to check the name on file",
              ]} />
              <Tip text="GSM stores names as Surname Firstname. If your organiser has you as Sellman Michael, register with First Name: Michael, Last Name: Sellman." />
            </Sub>

            <Sub title="12.3 The Unified Login Page">
              <P text="golfsocietymanager.com/choose-login shows a choice page with two options:" />
              <Tbl headers={["Option","Who Uses It","Where It Goes"]} rows={[
                ["Player","Society members","Player login and registration"],
                ["Society Organiser","Competition organisers","Main GSM admin login"],
              ]} />
            </Sub>

            <Sub title="12.4 Organiser Setup — Before the Round">
              <P text="The organiser must complete these steps before the competition starts:" />
              <Tbl headers={["Step","Screen","Action"]} rows={[
                ["1","Players","Set format to Singles Stableford, Medal or Stroke Play"],
                ["2","Players","Tick HCP Review checkbox for each player submitting for handicap review"],
                ["3","Groups","Run Auto Group to assign players to groups"],
              ]} />
              <P text="Marker assignment is not set up by the organiser — players declare their own marker at the tee (see 12.5 below)." />
            </Sub>

            <Sub title="12.5 At the Tee — Marker Declaration">
              <P text="Just as in real golf, players agree amongst themselves who is marking who at the first tee — GSM doesn't assign this in advance." />
              <Ol items={[
                "Before teeing off, agree with your group who is marking who, as normal",
                "Log in to the Player Portal (golfsocietymanager.com/choose-login) and go to the dashboard",
                "Tap Who's Marking Today",
                "Tap the name of the groupmate whose card you're marking to declare it",
                "If someone is already assigned to them, tap once more to confirm you're taking over",
                "Play the round as normal — mark your assigned partner's card hole by hole",
              ]} />
              <Tip text="A declaration can be changed any time before the round is archived — useful if pairings change on the day." />
            </Sub>

            <Sub title="12.6 After the Round — Player Verification">
              <P text="Once scores are entered, the marker (Player B) logs in to verify:" />
              <Ol items={[
                "Visit golfsocietymanager.com/choose-login and log in as a Player",
                "From the dashboard, tap Verify a Playing Partner",
                "Select the date of the competition from the list",
                "The system shows only the player you are assigned to mark",
                "Tap the player name to confirm their scorecard is correct",
                "The score is immediately locked and cannot be changed",
              ]} />
              <Tip text="Only the player you declared yourself as marker for will appear. If you see nobody listed, go to Who's Marking Today first and declare it." />
            </Sub>

            <Sub title="12.7 Verified Badge on Results">
              <P text="Once a score is verified, the Results page shows a green badge next to the player name:" />
              <Ul items={[
                "Verified by Marker Name — appears in the HCP Review section of the Results page",
                "The Export HCP Review button downloads an Excel file of all qualifying scores including verification status",
                "Verified scores are highlighted with a green border on the player My Results page",
              ]} />
            </Sub>

            <Sub title="12.8 Score Locking">
              <P text="Once a score is verified it is permanently locked:" />
              <Ul items={[
                "The player row in Score Entry shows a lock icon and a red background",
                "Clicking the row shows: Score locked — verified by name",
                "The Apply button is disabled for locked players",
                "Save Scores and Clear All skip locked players automatically",
              ]} />
            </Sub>

            <Sub title="12.9 Admin Unlock">
              <P text="If a genuine error is found after verification, the organiser can unlock a score:" />
              <Ol items={[
                "Go to the Results page",
                "Find the verified player in the HCP Review section",
                "Click the Unlock button next to their verified badge",
                "Enter a reason for unlocking (required)",
                "Click Confirm Unlock",
                "The score is unlocked and can be edited again — the player must re-verify after correction",
              ]} />
              <Warn text="All unlocks are permanently logged with the reason and timestamp. This provides a full audit trail of every change." />
            </Sub>

            <Sub title="12.10 My Results (Player View)">
              <P text="Players can view their own competition history from the Player Portal:" />
              <Ol items={[
                "Log in at golfsocietymanager.com/choose-login",
                "Tap View My Results",
                "See all archived competitions with position, points, gross and net scores",
                "Verified competitions show a Verified by name badge",
                "Unverified competitions show as Unverified",
              ]} />
            </Sub>

            <Sub title="12.11 Verification Portal Summary">
              <Tbl headers={["URL","Purpose","Who Uses It"]} rows={[
                ["golfsocietymanager.com/choose-login","Unified login choice page","Everyone"],
                ["golfsocietymanager.com/player/login","Player login","Society members"],
                ["golfsocietymanager.com/player/register","Player registration","New members"],
                ["golfsocietymanager.com/player/dashboard","Player dashboard","Logged-in players"],
                ["golfsocietymanager.com/player/declare-marker","Declare who you're marking","Logged-in players"],
                ["golfsocietymanager.com/player/hcp-verify","Verify a partners card","Assigned markers"],
                ["golfsocietymanager.com/player/my-results","View own results","Logged-in players"],
              ]} />
            </Sub>
          </Section>

          <Section title="13. 💻 V5 Desktop App — Offline Mode">
            <P text="The V5 Desktop App is an installable Windows program that lets you run a full competition day with no internet connection at all — ideal for courses with poor or no mobile signal." />

            <Sub title="13.1 Installing">
              <Ol items={[
                "Download the installer from golfsocietymanager.com (Download link in the sidebar)",
                "Run it — Windows may show a security warning since this is a new publisher; click \"More info\" then \"Run anyway\" to continue",
                "Choose an install location (or accept the default) and finish the install",
                "A Desktop and/or Start Menu shortcut is created — launch the app from there",
              ]} />
            </Sub>

            <Sub title="13.2 Activation">
              <P text="The very first time you launch the app, you'll be asked to activate it with a free code — this is required every time, there's no way to skip it." />
              <Ol items={[
                "Email support@golfsocietymanager.com with your Society name",
                "You'll receive a free activation code (format GSM-XXXXXX-XXXXXX-XXXXXX)",
                "Enter it on the activation screen and click Activate",
              ]} />
              <P text="Codes currently need renewing roughly monthly while the app is free, ahead of this becoming a paid subscription — just email support again for a new one when yours expires, it only takes a minute. Once activated, the app remembers your code on that computer, so future launches work even with no internet at all." />
            </Sub>

            <Sub title="13.3 Sync Now">
              <P text="Before you lose signal (e.g. in the car park or clubhouse), click <strong>Sync Now</strong> in the left sidebar. This does two things, in order:" />
              <Ol items={[
                "<strong>Pulls down</strong> your current event, players, groups and course/holes from the website into the app, so everything needed for the round is stored on this computer",
                "<strong>Pushes up</strong> any scores you've entered on this computer since the last sync, so they reach the website's records",
              ]} />
              <Warn text="Always run Sync Now once while you still have a connection, before heading out onto the course — this is what makes offline scoring possible." />
            </Sub>

            <Sub title="13.4 Working Offline">
              <P text="Once synced, the app works exactly like the website — view players, groups and the course, enter scores hole by hole, and save — all with zero internet connection required." />
            </Sub>

            <Sub title="13.5 Syncing Back Up">
              <P text="When you're back in signal range, click <strong>Sync Now</strong> again. Any scores entered while offline are sent up to golfsocietymanager.com. It's safe to click Sync Now as many times as you like — re-syncing never creates duplicate scores." />
            </Sub>

            <Sub title="13.6 What Doesn't Work Offline">
              <P text="Setup and admin tasks — adding/editing players, changing course details, exporting/importing the database, and licence/account management — need an internet connection, since they're managed centrally on the website. Only viewing your roster/course and entering scores work fully offline." />
            </Sub>
          </Section>

          <Section title="14. 📱 Android App">
            <P text="A native Android app, for running GSM on a tablet or phone out on the course — built for score entry specifically, with the same offline safety net as the Desktop App." />

            <Sub title="14.1 Installing">
              <Ol items={[
                "Not on the Google Play Store yet — your organiser sends you the app file (an .apk) directly, e.g. by email or a shared link, rather than you searching for it in an app store.",
                "On the Android device, open Settings → Security (wording varies by phone) and allow installs from the source you'll open the file from (e.g. your browser or file manager) — Android will prompt for this automatically the first time if you skip it.",
                "Open the .apk file and tap Install.",
              ]} />
            </Sub>

            <Sub title="14.2 Signing In">
              <P text="Sign in with the same credentials as the website — an organiser login, or a player's own Player Portal login. There's no separate Android-only account." />
            </Sub>

            <Sub title="14.3 Using the App">
              <P text="Once signed in, it works like the website — Players, Groups, Course, Score Entry, Results, Leaderboard and Player Stats are all here. The screens aren't specially resized for small phones yet, so a tablet gives the most comfortable view, though a phone works too." />
            </Sub>

            <Sub title="14.4 Working Offline">
              <P text="If you lose signal mid-round, score entry keeps working — scores you enter are safely queued on the device and sent automatically the moment signal returns, with no button to remember to press." />
              <P text="Setup and admin screens — Players, Groups, Teams and Course — need an internet connection, the same limitation as the Desktop App. Only score entry has the offline safety net." />
            </Sub>

            <Sub title="14.5 Updates">
              <P text="Since the app isn't on the Play Store, it doesn't update itself automatically. When a new version is available, your organiser will send you a new file to install the same way as the first time." />
            </Sub>
          </Section>

          <Section title="15. 📧 Member Communication & Player Accounts">
            <Sub title="15.1 Co-Organiser Logins">
              <P text="A second person can be given full organiser access under their own email and password, instead of sharing your login." />
              <Ol items={[
                "Your organiser sets up a co-organiser account for you with a temporary password",
                "Log in as normal at the sign-in screen using your own email and that temporary password",
                "You'll be asked to set your own permanent password on first login",
                "From then on, you have exactly the same access as any other organiser",
              ]} />
            </Sub>

            <Sub title="15.2 Mail Merge + One-Click Voting">
              <P text="Send a real email to your members with merge fields (like their first name) filled in automatically, and let them vote for the next event with a single tap — no login needed." />
              <Ol items={[
                "Go to <strong>Mail Merge</strong>, pick recipients (defaults to everyone with an email on file who hasn't voted yet)",
                "Write your subject and message using <strong>{{merge_tags}}</strong> for anything personalised",
                "Preview it rendered for a real player before sending",
                "Send — anyone without an email on file is skipped and reported, not silently dropped",
              ]} />
              <P text="Each email includes one-tap voting links — Golf & Dinner, Golf Only, Dinner Only, and Buggy — so a member can respond straight from their inbox with no login and no form to fill in." />
              <Tip text="Tapping a second link doesn't wipe out an earlier answer — voting Buggy after already voting Golf Only keeps both, since each link only ever changes the one thing it represents." />
            </Sub>

            <Sub title="15.3 Vote Results">
              <P text="The <strong>Vote Results</strong> page gives a dedicated, always-reliable summary of who has voted for the next event and what they chose." />
            </Sub>

            <Sub title="15.4 Link Accounts">
              <P text="If a member registers for the Player Portal and their typed name doesn't exactly match your roster (a spelling difference, a missing initial), their account is created but not automatically connected to their history. The <strong>Link Accounts</strong> page shows any account waiting to be matched — pick the right player from a dropdown and click Link." />
            </Sub>

            <Sub title="15.5 Tuesday Golf">
              <P text="Tuesday Golf is a separate sign-up page (tues.golfsocietymanager.com) within the same app, where members can add or remove themselves from a slot using the sign-up PIN. Changing the round's actual date or tee time needs a separate organiser PIN, set in Changeable Settings, so a member can't accidentally move everyone else's booking." />
            </Sub>
          </Section>

          <Section title="16. 📅 Fixture & Event Planner">
            <P text="Plan a fixture well ahead of time, let members sign themselves up, manage a waiting list if it fills up, then turn it into a real competition with one click when you're ready — all before anyone touches Players, Groups or Score Entry." />

            <Sub title="16.1 Creating a Fixture">
              <P text="Go to <strong>Planner</strong> in the sidebar and click <strong>+ New Fixture</strong>. Fill in the event name, date, venue, course, format, optional HCP cap, first tee time and interval, an optional cap on the number of golfers, a closing date, and any costs you want shown to members (golf, dinner, buggy, other)." />
              <P text="A new fixture starts as a <strong>Draft</strong> — nothing is visible to members until you publish it." />
            </Sub>

            <Sub title="16.2 Publishing">
              <P text="Open the fixture and click <strong>Publish</strong>. This is the only way a Draft becomes visible to members — you can still edit any other detail at any time afterwards, right up until the fixture is made the current competition." />
            </Sub>

            <Sub title="16.3 How Members Sign Up">
              <P text="There are three ways to respond, and all three do exactly the same thing underneath:" />
              <Ul items={[
                "The public signup page (a shared link for your society, no login needed) — type a surname, pick yourself from the matching list, then choose.",
                "The Player Portal — a logged-in member sees a \"Next Event\" card right on their dashboard with the same choices, no searching needed.",
                "One-click email links sent from the Communications tab (see 16.6) — tapping a link registers the choice immediately.",
              ]} />
              <P text="The choice itself is deliberately simple — just two buttons, <strong>Sign Up</strong> and <strong>Buggy Required</strong> (which signs up and requests a buggy in one tap), plus <strong>Can't make it</strong>. A member can change their mind as many times as they like before the closing date — switching back and forth between signed up, buggy, and declined all work exactly as you'd expect." />
            </Sub>

            <Sub title="16.4 Capacity and the Reserve List">
              <P text="If you've set a cap on the number of golfers and it's already reached, the next person to sign up lands on the <strong>Reserve list</strong> instead, numbered in the order they signed up. Reserve promotion is always something you do by hand from the Reserves tab — nobody is ever promoted automatically, since circumstances may have changed by the time a place opens up." />
              <P text="Once the closing date passes, members can no longer sign themselves up or change their own answer — but you can still add, change, or promote anyone yourself at any time." />
            </Sub>

            <Sub title="16.5 Managing a Fixture">
              <P text="Click any fixture to open its dashboard, with five tabs:" />
              <Ul items={[
                "<strong>Players</strong> — everyone confirmed, with a <strong>+ Add Player</strong> button for registering a response yourself on behalf of a member who phoned or texted you instead of using the digital tools.",
                "<strong>Reserves</strong> — the waiting list, with a Promote button for each.",
                "<strong>Costs</strong> — shown as Expected Cost (not Amount Due, since GSM doesn't take payments yet), broken down per category with a total.",
                "<strong>Tee Times</strong> — a draft tee sheet generated from your first tee time and interval; click any time to adjust it if the club's given you an odd gap.",
                "<strong>Communications</strong> — one-click filters (Invite Members, Email Non-Responders, Email Confirmed, Email Reserve List) for sending the signup links by email.",
              ]} />
            </Sub>

            <Sub title="16.6 Make Current Competition">
              <P text="When you're ready, click <strong>Make Current Competition</strong>. This creates a real competition from the fixture, ticks every confirmed signup as Playing on the Players screen (carrying over their buggy flag), and deactivates whatever competition was previously current. From that point it behaves exactly like any other GSM competition — Groups, Score Entry, Results and Archive all work exactly as normal." />
              <Tip text="Clicking it a second time does nothing harmful — the fixture shows it's already linked to a competition, with a button to jump straight to it." />
            </Sub>

            <Sub title="16.7 Deleting a Fixture">
              <P text="A Draft or Open fixture can be deleted from the Planner list or from its own dashboard — this removes the fixture and every signup against it, and can't be undone. Once a fixture has been made the current competition, it can no longer be deleted here, since it's now the permanent record of what was transferred into that competition." />
            </Sub>
          </Section>

          <div className="mt-8 pt-4 border-t border-gray-200 text-xs text-gray-400 text-center">
            <p className="mb-1"><a href="/release-notes" className="text-blue-600 hover:underline">Looking for the full version history? See Release Notes</a></p>
            <p className="mb-2"><a href="/whs-overview" className="text-blue-600 hover:underline">GSM WHS Technical Overview</a></p>
            <p>© 2026 Michael Sellman. Golf Society Manager. All rights reserved.</p>
            <p className="italic">Unauthorised copying, distribution or modification of this guide is strictly prohibited.</p>
          </div>

        </div>
      </div>
    </Layout>
  );
}
